import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    MenuItem,
    OutlinedInput,
    Select,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import axiosInstance from '../../../helpers/axios';
import { formatCurrency } from '../utils/format';
import { MONTH_NAMES } from '../utils/month';
import { downloadFile } from '../utils/downloadFile';
import { useInstituteOptions } from '../hooks/useInstituteOptions';
import { fibeTokens } from '../../../theme';
import { FibeDialog, FibeSegmentedToggle, DialogAlert, fieldLabelSx } from '../components/FibeDialog';

const AGENT_PAGE_SIZE = 50;

function computeEarned(agent) {
    return Number(agent.totalPayable) + Number(agent.totalPaid) + Number(agent.totalHeld) + Number(agent.totalForfeited);
}

const MENU_PROPS = {
    PaperProps: {
        sx: {
            '& .MuiMenuItem-root': {
                fontFamily: fibeTokens.font.family,
                fontSize: 12.5,
                fontWeight: 400,
                minHeight: 'auto',
                padding: '8px 12px',
            },
        },
    },
};

function RefreshGlyph() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12a9 9 0 1 1-3-6.7" />
            <path d="M21 4v5h-5" />
        </svg>
    );
}

function PersonGlyph() {
    return (
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20a8 8 0 0 1 16 0" />
        </svg>
    );
}

function PersonAddGlyph() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="10" cy="8" r="3.2" />
            <path d="M3.5 20a6.5 6.5 0 0 1 13 0" />
            <path d="M19 8v6M22 11h-6" />
        </svg>
    );
}

function ChainLinkGlyph() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
            <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
        </svg>
    );
}

function ChainBreakGlyph() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
            <path d="m3 3 18 18" />
        </svg>
    );
}

function CardGlyph() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="6" width="20" height="13" rx="2.5" />
            <path d="M2 10h20" />
        </svg>
    );
}

function ErrorBanner({ title, message, onRetry, retryLabel = 'Retry' }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '14px',
                background: fibeTokens.color.dangerTint,
                border: `1px solid ${fibeTokens.color.dangerBorder}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                padding: fibeTokens.density.cardPadding,
            }}
        >
            <Box>
                <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 13, color: fibeTokens.color.danger }}>
                    {title}
                </Box>
                <Box sx={{ ...fibeTokens.font.helper, fontFamily: fibeTokens.font.family, color: fibeTokens.color.danger, marginTop: '4px' }}>
                    {message}
                </Box>
            </Box>
            {onRetry && (
                <Box sx={{ flex: 'none' }}>
                    <Button variant="fibeSecondary" size="small" onClick={onRetry}>{retryLabel}</Button>
                </Box>
            )}
        </Box>
    );
}

function RegisterSubAgentModal({ onClose, onRegistered }) {
    const [agentCode, setAgentCode] = useState('');
    const [name, setName] = useState('');
    const [ratio, setRatio] = useState(80);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const handleSubmit = () => {
        setIsSubmitting(true);
        setErrorMessage(null);
        axiosInstance
            .post('/fibe-billing/sub-agents', { agentCode, name, ratio: Number(ratio) })
            .then(() => {
                onRegistered();
                onClose();
            })
            .catch((error) => setErrorMessage(error.response?.data?.message ?? 'Could not register sub-agent.'))
            .finally(() => setIsSubmitting(false));
    };

    const agentPct = Number(ratio) || 0;
    const feemonkPct = 100 - agentPct;

    return (
        <FibeDialog
            open
            onClose={onClose}
            tone="red"
            icon={<PersonAddGlyph />}
            title="Register New Sub-Agent"
            subtitle="Sets the commission split for all future linked loans"
            footer={(
                <>
                    <Button variant="fibeSecondary" onClick={onClose}>Cancel</Button>
                    <Button variant="fibePrimary" onClick={handleSubmit} disabled={isSubmitting || !agentCode || !name}>
                        {isSubmitting ? 'Registering…' : 'Register Sub-Agent'}
                    </Button>
                </>
            )}
        >
            <Box sx={{ display: 'flex', gap: '12px' }}>
                <Box sx={{ flex: 1 }}>
                    <Box component="label" sx={fieldLabelSx()}>Agent Code</Box>
                    <OutlinedInput size="fibeMd" fullWidth placeholder="e.g. SA-005" value={agentCode} onChange={(e) => setAgentCode(e.target.value)} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Box component="label" sx={fieldLabelSx()}>Agent Name</Box>
                    <OutlinedInput size="fibeMd" fullWidth placeholder="e.g. Epsilon Partners" value={name} onChange={(e) => setName(e.target.value)} />
                </Box>
            </Box>

            <Box>
                <Box component="label" sx={fieldLabelSx()}>Commission Ratio (Agent's Share %)</Box>
                <OutlinedInput size="fibeMd" fullWidth type="number" inputProps={{ min: 1, max: 99 }} value={ratio} onChange={(e) => setRatio(e.target.value)} />
                <Box sx={{ ...fibeTokens.font.helper, fontFamily: fibeTokens.font.family, marginTop: '6px' }}>
                    Agent gets {ratio || 0}%, FeeMonk retains {feemonkPct}%
                </Box>
                <Box sx={{ display: 'flex', gap: '9px', marginTop: '11px' }}>
                    <Box sx={{ flex: agentPct || 1, padding: '10px 12px', background: fibeTokens.color.successTint, border: `1px solid ${fibeTokens.color.success}`, borderRadius: '10px' }}>
                        <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 16, color: fibeTokens.color.success }}>{agentPct}%</Box>
                        <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 500, fontSize: 10.5, color: fibeTokens.color.success, marginTop: '2px' }}>Agent</Box>
                    </Box>
                    <Box sx={{ flex: feemonkPct || 1, padding: '10px 12px', background: fibeTokens.color.rowHover, border: `1px solid ${fibeTokens.color.border}`, borderRadius: '10px' }}>
                        <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 16, color: fibeTokens.color.textSecondary }}>{feemonkPct}%</Box>
                        <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 500, fontSize: 10.5, color: fibeTokens.color.textMuted, marginTop: '2px' }}>FeeMonk</Box>
                    </Box>
                </Box>
            </Box>

            {errorMessage && <ErrorBanner title="Could not register sub-agent" message={errorMessage} />}
        </FibeDialog>
    );
}

function LinkSubAgentModal({ agents, institutes, initialAgent, onClose, onLinked }) {
    const [agentId, setAgentId] = useState(initialAgent?.id ?? agents[0]?.id ?? '');
    const [linkType, setLinkType] = useState('institute');
    const [instituteId, setInstituteId] = useState('');
    const [applicationId, setApplicationId] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const handleSubmit = () => {
        setIsSubmitting(true);
        setErrorMessage(null);
        const payload = linkType === 'institute' ? { instituteId } : { applicationId };
        axiosInstance
            .post(`/fibe-billing/sub-agents/${agentId}/links`, payload)
            .then(() => {
                onLinked();
                onClose();
            })
            .catch((error) => setErrorMessage(error.response?.data?.message ?? 'Could not save link.'))
            .finally(() => setIsSubmitting(false));
    };

    const canSubmit = linkType === 'institute' ? !!instituteId : !!applicationId;

    return (
        <FibeDialog
            open
            onClose={onClose}
            tone="blue"
            icon={<ChainLinkGlyph />}
            title="Link Sub-Agent"
            subtitle="To an institute (all future loans) or one specific loan"
            footer={(
                <>
                    <Button variant="fibeSecondary" onClick={onClose}>Cancel</Button>
                    <Button variant="fibePrimary" onClick={handleSubmit} disabled={isSubmitting || !canSubmit}>
                        {isSubmitting ? 'Saving…' : 'Save Link'}
                    </Button>
                </>
            )}
        >
            <Box>
                <Box component="label" sx={fieldLabelSx()}>Sub-Agent</Box>
                <Select size="fibeMd" fullWidth value={agentId} onChange={(e) => setAgentId(e.target.value)} MenuProps={MENU_PROPS}>
                    {agents.map((agent) => (
                        <MenuItem key={agent.id} value={agent.id}>
                            {agent.agentCode} — {agent.name} ({agent.ratio}:{100 - agent.ratio})
                        </MenuItem>
                    ))}
                </Select>
            </Box>

            <Box>
                <Box component="label" sx={fieldLabelSx()}>Link Type</Box>
                <FibeSegmentedToggle
                    value={linkType}
                    onChange={setLinkType}
                    options={[
                        { value: 'institute', label: 'By Institute' },
                        { value: 'loan', label: 'By Individual Loan' },
                    ]}
                />
            </Box>

            {linkType === 'institute' ? (
                <Box>
                    <Box component="label" sx={fieldLabelSx()}>Institute</Box>
                    <Select size="fibeMd" fullWidth displayEmpty value={instituteId} onChange={(e) => setInstituteId(e.target.value)} MenuProps={MENU_PROPS}>
                        <MenuItem value="">Select an institute…</MenuItem>
                        {institutes.map((institute) => (
                            <MenuItem key={institute.id} value={institute.id}>{institute.brandName}</MenuItem>
                        ))}
                    </Select>
                </Box>
            ) : (
                <Box>
                    <Box component="label" sx={fieldLabelSx()}>Application ID</Box>
                    <OutlinedInput size="fibeMd" fullWidth placeholder="FM26015869" value={applicationId} onChange={(e) => setApplicationId(e.target.value)} />
                </Box>
            )}

            {errorMessage && <ErrorBanner title="Could not save link" message={errorMessage} />}
        </FibeDialog>
    );
}

function DelinkSubAgentModal({ agent, instituteNameById, onClose, onDelinked }) {
    const [links, setLinks] = useState([]);
    const [selectedLinkId, setSelectedLinkId] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const loadLinks = useCallback(() => {
        setIsLoading(true);
        setLoadError(null);
        axiosInstance
            .get(`/fibe-billing/sub-agents/${agent.id}/links`)
            .then((response) => {
                const fetchedLinks = response.data.data;
                setLinks(fetchedLinks);
                setSelectedLinkId(fetchedLinks[0]?.id ?? '');
            })
            .catch((error) => setLoadError(error.response?.data?.message ?? 'Could not load this agent’s links.'))
            .finally(() => setIsLoading(false));
    }, [agent.id]);

    useEffect(() => {
        loadLinks();
    }, [loadLinks]);

    const linkLabel = (link) => (link.instituteId
        ? `Institute: ${instituteNameById.get(link.instituteId) ?? link.instituteId}`
        : `Loan: ${link.applicationId}`);

    const handleSubmit = () => {
        setIsSubmitting(true);
        setErrorMessage(null);
        axiosInstance
            .delete(`/fibe-billing/sub-agents/${agent.id}/links/${selectedLinkId}`)
            .then(() => {
                onDelinked();
                onClose();
            })
            .catch((error) => setErrorMessage(error.response?.data?.message ?? 'Could not remove link.'))
            .finally(() => setIsSubmitting(false));
    };

    return (
        <FibeDialog
            open
            onClose={onClose}
            tone="danger"
            icon={<ChainBreakGlyph />}
            title={`De-link Sub-Agent — ${agent.name}`}
            subtitle="Affects future loans only"
            footer={(
                <>
                    <Button variant="fibeSecondary" onClick={onClose}>Cancel</Button>
                    <Button
                        variant="fibeDangerSolid"
                        onClick={handleSubmit}
                        disabled={isSubmitting || isLoading || links.length === 0}
                    >
                        {isSubmitting ? 'Removing…' : 'Confirm De-link'}
                    </Button>
                </>
            )}
        >
            <DialogAlert tone="danger">
                De-linking only affects <strong>future loans</strong>. Existing payables for previously originated
                loans will continue through their lifecycle (pending → paid).
            </DialogAlert>

            {isLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', fontFamily: fibeTokens.font.family, fontSize: 12.5, color: fibeTokens.color.textMuted }}>
                    <CircularProgress size={14} /> Loading links…
                </Box>
            )}
            {!isLoading && loadError && <ErrorBanner title="Could not load this agent’s links" message={loadError} onRetry={loadLinks} />}
            {!isLoading && !loadError && links.length === 0 && (
                <Box sx={{ fontFamily: fibeTokens.font.family, fontSize: 12.5, color: fibeTokens.color.textMuted }}>
                    This agent has no links to remove.
                </Box>
            )}
            {!isLoading && !loadError && links.length > 0 && (
                <Box>
                    <Box component="label" sx={fieldLabelSx()}>De-link From</Box>
                    <Select size="fibeMd" fullWidth value={selectedLinkId} onChange={(e) => setSelectedLinkId(e.target.value)} MenuProps={MENU_PROPS}>
                        {links.map((link) => (
                            <MenuItem key={link.id} value={link.id}>{linkLabel(link)}</MenuItem>
                        ))}
                    </Select>
                </Box>
            )}

            {errorMessage && <ErrorBanner title="Could not remove link" message={errorMessage} />}
        </FibeDialog>
    );
}

function PaySubAgentModal({ agent, onClose, onPaid }) {
    const [payables, setPayables] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [paymentReference, setPaymentReference] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const loadPayables = useCallback(() => {
        setIsLoading(true);
        setLoadError(null);
        axiosInstance
            .get(`/fibe-billing/sub-agents/${agent.id}/payables`, { params: { status: 'pending', limit: 50 } })
            .then((response) => {
                const rows = response.data.data;
                setPayables(rows);
                setSelectedIds(new Set(rows.map((row) => row.id)));
            })
            .catch((error) => setLoadError(error.response?.data?.message ?? 'Could not load pending payables.'))
            .finally(() => setIsLoading(false));
    }, [agent.id]);

    useEffect(() => {
        loadPayables();
    }, [loadPayables]);

    const toggleSelected = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const totalSelected = payables
        .filter((payable) => selectedIds.has(payable.id))
        .reduce((sum, payable) => sum + Number(payable.amount), 0);

    const handleSubmit = () => {
        setIsSubmitting(true);
        setErrorMessage(null);
        axiosInstance
            .post(`/fibe-billing/sub-agents/${agent.id}/payables/mark-paid`, {
                payableIds: [...selectedIds],
                paymentReference,
            })
            .then(() => {
                onPaid();
                onClose();
            })
            .catch((error) => setErrorMessage(error.response?.data?.message ?? 'Could not record payment.'))
            .finally(() => setIsSubmitting(false));
    };

    return (
        <FibeDialog
            open
            onClose={onClose}
            wide
            tone="green"
            icon={<CardGlyph />}
            title={`Pay Sub-Agent — ${agent.name} (${agent.agentCode})`}
            subtitle="Select the pending payables to settle"
            footer={(
                <>
                    <Button variant="fibeSecondary" onClick={onClose}>Cancel</Button>
                    <Button
                        variant="fibeSuccess"
                        onClick={handleSubmit}
                        disabled={isSubmitting || selectedIds.size === 0 || !paymentReference}
                    >
                        {isSubmitting ? 'Confirming…' : 'Confirm Payment'}
                    </Button>
                </>
            )}
        >
            <DialogAlert tone="info">
                Max payable right now: <strong>{formatCurrency(agent.totalReceived - agent.totalPaid)}</strong>{' '}
                (Received from Fibe − Already Paid). The server rejects a selection that exceeds this.
            </DialogAlert>

            {isLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', fontFamily: fibeTokens.font.family, fontSize: 12.5, color: fibeTokens.color.textMuted }}>
                    <CircularProgress size={14} /> Loading pending payables…
                </Box>
            )}
            {!isLoading && loadError && <ErrorBanner title="Could not load pending payables" message={loadError} onRetry={loadPayables} />}
            {!isLoading && !loadError && payables.length === 0 && (
                <Box sx={{ fontFamily: fibeTokens.font.family, fontSize: 12.5, color: fibeTokens.color.textMuted }}>
                    No pending payables for this agent.
                </Box>
            )}
            {!isLoading && !loadError && payables.length > 0 && (
                <Box sx={{ border: `1px solid ${fibeTokens.color.border}`, borderRadius: '10px', overflow: 'hidden' }}>
                    <Table sx={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell size="fibe" variant="head" sx={{ width: 38 }}></TableCell>
                                <TableCell size="fibe" variant="head" align="left" sx={{ width: 150 }}>App ID</TableCell>
                                <TableCell size="fibe" variant="head" align="left">Milestone</TableCell>
                                <TableCell size="fibe" variant="head" align="right" sx={{ width: 130 }}>Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {payables.map((payable) => (
                                <TableRow key={payable.id}>
                                    <TableCell size="fibe" variant="body">
                                        <Checkbox
                                            size="small"
                                            checked={selectedIds.has(payable.id)}
                                            onChange={() => toggleSelected(payable.id)}
                                            sx={{ padding: 0, color: fibeTokens.color.primary, '&.Mui-checked': { color: fibeTokens.color.primary } }}
                                        />
                                    </TableCell>
                                    <TableCell size="fibe" variant="body" align="left" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11.5, color: fibeTokens.color.textSecondary }}>
                                        {payable.applicationId}
                                    </TableCell>
                                    <TableCell size="fibe" variant="body" align="left">{payable.milestoneType}</TableCell>
                                    <TableCell size="fibe" variant="body" align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                        {formatCurrency(payable.amount)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: '12px' }}>
                <Box sx={{ flex: 1 }}>
                    <Box component="label" sx={fieldLabelSx()}>Total Payment</Box>
                    <OutlinedInput
                        size="fibeMd"
                        fullWidth
                        readOnly
                        value={formatCurrency(totalSelected)}
                        sx={{ background: fibeTokens.color.rowHover, fontWeight: 600, color: fibeTokens.color.textPrimary }}
                    />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Box component="label" sx={fieldLabelSx()}>Payment Reference / UTR</Box>
                    <OutlinedInput size="fibeMd" fullWidth placeholder="NEFT/IMPS reference" value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} />
                </Box>
            </Box>

            {errorMessage && <ErrorBanner title="Could not record payment" message={errorMessage} />}
        </FibeDialog>
    );
}

function LedgerSkeleton() {
    return (
        <Box
            sx={{
                background: fibeTokens.color.surface,
                border: `1px solid ${fibeTokens.color.border}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                boxShadow: '0 6px 20px -10px rgba(16,24,40,.16)',
                overflow: 'hidden',
                marginBottom: '14px',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '13px 18px', background: fibeTokens.dialog.footerBg, borderBottom: `1px solid ${fibeTokens.dialog.footerBorder}` }}>
                <Skeleton variant="text" width={220} height={16} />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px', padding: '16px 18px 12px' }}>
                {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={58} sx={{ borderRadius: '10px' }} />
                ))}
            </Box>
        </Box>
    );
}

function LedgerPanel({ agent, onClose }) {
    const [ledger, setLedger] = useState(null);
    const [ledgerError, setLedgerError] = useState(null);
    const [statement, setStatement] = useState(null);
    const [actionError, setActionError] = useState(null);
    const now = new Date();
    const [reportMonth, setReportMonth] = useState(now.getMonth() + 1);
    const [reportYear, setReportYear] = useState(now.getFullYear());

    const loadLedger = useCallback(() => {
        setLedgerError(null);
        axiosInstance
            .get(`/fibe-billing/sub-agents/${agent.id}/ledger`)
            .then((response) => setLedger(response.data.data))
            .catch((error) => setLedgerError(error.response?.data?.message ?? 'Could not load this agent’s ledger.'));
    }, [agent.id]);

    useEffect(() => {
        loadLedger();
    }, [loadLedger]);

    const handleViewStatement = () => {
        setActionError(null);
        axiosInstance
            .get(`/fibe-billing/sub-agents/${agent.id}/statement`, { params: { month: reportMonth, year: reportYear } })
            .then((response) => setStatement(response.data.data))
            .catch((error) => setActionError(error.response?.data?.message ?? 'Could not load statement.'));
    };

    const handleExportReport = () => {
        setActionError(null);
        downloadFile(
            `/fibe-billing/sub-agents/${agent.id}/report?month=${reportMonth}&year=${reportYear}`,
            `sub-agent-${agent.id}-report-${reportYear}-${String(reportMonth).padStart(2, '0')}.xlsx`,
        ).catch((error) => setActionError(error.response?.data?.message ?? 'Could not export report.'));
    };

    if (ledgerError) {
        return (
            <Box
                sx={{
                    background: fibeTokens.color.surface,
                    border: `1px solid ${fibeTokens.color.border}`,
                    borderRadius: `${fibeTokens.density.cardRadius}px`,
                    boxShadow: fibeTokens.density.cardShadow,
                    padding: fibeTokens.density.cardPadding,
                    marginBottom: '14px',
                }}
            >
                <ErrorBanner title="Could not load this agent’s ledger" message={ledgerError} onRetry={loadLedger} />
                <Box sx={{ marginTop: '12px' }}>
                    <Button variant="fibeSecondary" size="small" onClick={onClose}>Close</Button>
                </Box>
            </Box>
        );
    }

    if (!ledger) {
        return <LedgerSkeleton />;
    }

    const netBalance = Number(ledger.totalReceived) - Number(ledger.totalPaid);

    const stats = [
        { label: 'Receivable', value: formatCurrency(ledger.totalReceivable) },
        { label: 'Received', value: formatCurrency(ledger.totalReceived), tone: fibeTokens.color.success },
        { label: 'Payable', value: formatCurrency(ledger.totalPayable) },
        { label: 'Paid', value: formatCurrency(ledger.totalPaid), tone: fibeTokens.color.success },
        { label: 'Held', value: formatCurrency(ledger.totalHeld), tone: fibeTokens.color.warning },
        { label: 'Net Balance', value: formatCurrency(netBalance), tone: fibeTokens.color.info },
    ];

    return (
        <Box
            sx={{
                background: fibeTokens.color.surface,
                border: `1px solid ${fibeTokens.color.border}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                boxShadow: '0 6px 20px -10px rgba(16,24,40,.16)',
                overflow: 'hidden',
                marginBottom: '14px',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '13px 18px', background: fibeTokens.dialog.footerBg, borderBottom: `1px solid ${fibeTokens.dialog.footerBorder}`, flexWrap: 'wrap' }}>
                <Box sx={{ width: '3px', height: '20px', borderRadius: '9px', background: fibeTokens.color.info, flex: 'none' }} />
                <Box component="h2" sx={{ margin: 0, fontFamily: fibeTokens.font.family, ...fibeTokens.font.sectionHeading, color: fibeTokens.color.textPrimary }}>
                    Ledger — {agent.name} <Box component="span" sx={{ color: fibeTokens.color.textDisabled, fontWeight: 500 }}>({agent.agentCode})</Box>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Select size="fibeSm" value={reportMonth} onChange={(e) => setReportMonth(Number(e.target.value))} MenuProps={MENU_PROPS}>
                    {MONTH_NAMES.map((monthName, index) => (
                        <MenuItem key={monthName} value={index + 1}>{monthName}</MenuItem>
                    ))}
                </Select>
                <OutlinedInput size="fibeSm" type="number" value={reportYear} onChange={(e) => setReportYear(Number(e.target.value))} sx={{ width: 90 }} />
                <Button variant="fibePrimary" size="small" onClick={handleExportReport}>Export Billing Report</Button>
                <Button variant="fibeSecondary" size="small" onClick={handleViewStatement}>Statement</Button>
                <Button variant="fibeGhost" size="small" onClick={onClose}>Close</Button>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px', padding: '16px 18px 14px' }}>
                {stats.map((stat) => (
                    <Box key={stat.label} sx={{ border: `1px solid ${fibeTokens.color.border}`, borderRadius: '10px', padding: '11px 13px', background: fibeTokens.color.rowHover }}>
                        <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 10, color: fibeTokens.color.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            {stat.label}
                        </Box>
                        <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 18, color: stat.tone ?? fibeTokens.color.textPrimary, marginTop: '5px', letterSpacing: '-0.015em' }}>
                            {stat.value}
                        </Box>
                    </Box>
                ))}
            </Box>

            <Box sx={{ padding: '0 18px 18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <DialogAlert tone="warning">
                    <strong>Overpayment Guard</strong> — max payable right now = {formatCurrency(netBalance)} (Received from Fibe − Already Paid)
                </DialogAlert>

                {actionError && <ErrorBanner title="Action failed" message={actionError} />}

                {statement && (
                    <DialogAlert tone="info">
                        Statement for {MONTH_NAMES[statement.month - 1]} {statement.year}: Earned {formatCurrency(statement.earned)},{' '}
                        Paid {formatCurrency(statement.paid)}, Pending {formatCurrency(statement.pending)}, Held {formatCurrency(statement.held)},{' '}
                        Forfeited {formatCurrency(statement.forfeited)}
                    </DialogAlert>
                )}
            </Box>
        </Box>
    );
}

function PageHeader({ isLoading, onRefresh, onRegister }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
            <Box>
                <Box component="h1" sx={{ margin: 0, fontFamily: fibeTokens.font.family, ...fibeTokens.font.pageTitle, color: fibeTokens.color.textPrimary }}>
                    Sub-Agent Management
                </Box>
                <Box sx={{ margin: '5px 0 0', fontFamily: fibeTokens.font.family, ...fibeTokens.font.helper }}>
                    Commission splits, payables and per-agent ledgers
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                <Button
                    variant="fibeSecondary"
                    disabled={isLoading}
                    onClick={onRefresh}
                    startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshGlyph />}
                >
                    {isLoading ? 'Refreshing...' : 'Refresh'}
                </Button>
                <Button variant="fibePrimary" onClick={onRegister}>+ Register Sub-Agent</Button>
            </Box>
        </Box>
    );
}

function SubAgentsEmptyState({ onRegister }) {
    return (
        <Box sx={{ textAlign: 'center', padding: '40px 18px' }}>
            <Box
                sx={{
                    width: 42,
                    height: 42,
                    margin: '0 auto 12px',
                    borderRadius: '12px',
                    background: fibeTokens.color.surface,
                    border: `1px solid ${fibeTokens.color.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: fibeTokens.color.textDisabled,
                }}
            >
                <PersonGlyph />
            </Box>
            <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 13, color: fibeTokens.color.textPrimary }}>
                No sub-agents registered yet.
            </Box>
            <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 400, fontSize: 12, lineHeight: 1.6, color: fibeTokens.color.textMuted, margin: '5px 0 13px' }}>
                Register a sub-agent to start splitting commission on their linked institutes or loans.
            </Box>
            <Button variant="fibePrimary" size="small" onClick={onRegister}>+ Register Sub-Agent</Button>
        </Box>
    );
}

const AGENT_SKELETON_COLUMNS = [
    { width: 56 },
    { width: 110 },
    { width: 54, pill: true },
    { width: 150 },
    { width: 64 },
    { width: 64 },
    { width: 64 },
    { width: 50 },
    { width: 190, button: true },
];

function AgentsTableSkeleton() {
    return (
        <>
            <Box
                sx={{
                    height: `${fibeTokens.density.tableHeaderHeight}px`,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 14px',
                    background: fibeTokens.color.rowHover,
                    borderBottom: `1px solid ${fibeTokens.color.border}`,
                }}
            >
                <Skeleton variant="text" width={160} height={10} />
            </Box>
            {Array.from({ length: 5 }).map((_, rowIndex) => (
                <Box
                    key={rowIndex}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        height: `${fibeTokens.density.tableRowHeight}px`,
                        padding: '0 14px',
                        borderBottom: rowIndex < 4 ? `1px solid ${fibeTokens.color.border}` : 'none',
                    }}
                >
                    {AGENT_SKELETON_COLUMNS.map((col, colIndex) => (
                        <Skeleton
                            key={colIndex}
                            variant={col.pill || col.button ? 'rounded' : 'text'}
                            width={col.width}
                            height={col.pill ? 18 : col.button ? 22 : 12}
                            sx={{ borderRadius: col.pill ? '999px' : col.button ? '7px' : undefined, flex: 'none' }}
                        />
                    ))}
                </Box>
            ))}
        </>
    );
}

function SubAgentsTab() {
    const [agents, setAgents] = useState([]);
    const [linksByAgentId, setLinksByAgentId] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [modal, setModal] = useState(null);
    const [ledgerAgentId, setLedgerAgentId] = useState(null);

    const { institutes } = useInstituteOptions();
    const instituteNameById = useMemo(() => {
        const map = new Map();
        institutes.forEach((institute) => map.set(institute.id, institute.brandName));
        return map;
    }, [institutes]);

    const loadAgents = useCallback(() => {
        setIsLoading(true);
        setErrorMessage(null);
        axiosInstance
            .get('/fibe-billing/sub-agents/dashboard/summary', { params: { limit: AGENT_PAGE_SIZE } })
            .then((response) => {
                const rows = response.data.data;
                setAgents(rows);
                return Promise.all(
                    rows.map((agent) =>
                        axiosInstance
                            .get(`/fibe-billing/sub-agents/${agent.id}/links`)
                            .then((linksResponse) => [agent.id, linksResponse.data.data]),
                    ),
                );
            })
            .then((agentLinkPairs) => {
                setLinksByAgentId(Object.fromEntries(agentLinkPairs));
            })
            .catch(() => setErrorMessage('Could not load sub-agents.'))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        loadAgents();
    }, [loadAgents]);

    const closeModal = () => setModal(null);

    const linkedInstitutesLabel = (agentId) => {
        const links = linksByAgentId[agentId] ?? [];
        if (links.length === 0) return '—';
        return links
            .map((link) => (link.instituteId ? (instituteNameById.get(link.instituteId) ?? link.instituteId) : `Loan: ${link.applicationId}`))
            .join(', ');
    };

    const totals = agents.reduce(
        (acc, agent) => ({
            activeCount: acc.activeCount + (agent.isActive ? 1 : 0),
            totalPaid: acc.totalPaid + Number(agent.totalPaid),
            totalPending: acc.totalPending + Number(agent.totalPayable),
            netBalance: acc.netBalance + (Number(agent.totalReceived) - Number(agent.totalPaid)),
        }),
        { activeCount: 0, totalPaid: 0, totalPending: 0, netBalance: 0 },
    );

    const ledgerAgent = agents.find((agent) => agent.id === ledgerAgentId);

    const kpis = [
        { key: 'active', label: 'Active Sub-Agents', value: totals.activeCount, tone: fibeTokens.color.textSecondary },
        { key: 'paid', label: 'Total Paid', value: formatCurrency(totals.totalPaid), tone: fibeTokens.color.success },
        { key: 'pending', label: 'Pending Payable', value: formatCurrency(totals.totalPending), tone: fibeTokens.color.warning },
        { key: 'net', label: 'Net Balance', value: formatCurrency(totals.netBalance), tone: fibeTokens.color.info },
    ];

    return (
        <div>
            <PageHeader isLoading={isLoading} onRefresh={loadAgents} onRegister={() => setModal({ type: 'register' })} />

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: `${fibeTokens.density.gridGap}px`, marginBottom: '14px' }}>
                {kpis.map((kpi) => (
                    <Box
                        key={kpi.key}
                        sx={{
                            background: fibeTokens.color.surface,
                            border: `1px solid ${fibeTokens.color.border}`,
                            borderLeft: `3px solid ${kpi.tone}`,
                            borderRadius: `${fibeTokens.density.cardRadius}px`,
                            padding: fibeTokens.density.cardPadding,
                            boxShadow: fibeTokens.density.cardShadow,
                        }}
                    >
                        <Box sx={{ fontFamily: fibeTokens.font.family, ...fibeTokens.font.kpiLabel, marginBottom: '10px' }}>
                            {kpi.label}
                        </Box>
                        <Box sx={{ fontFamily: fibeTokens.font.family, ...fibeTokens.font.kpiValue, fontSize: 28, color: kpi.tone }}>
                            {kpi.value}
                        </Box>
                    </Box>
                ))}
            </Box>

            <Box
                sx={{
                    background: fibeTokens.color.surface,
                    border: `1px solid ${fibeTokens.color.border}`,
                    borderRadius: `${fibeTokens.density.cardRadius}px`,
                    boxShadow: fibeTokens.density.cardShadow,
                    overflow: 'hidden',
                    marginBottom: '14px',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', padding: fibeTokens.density.cardHeaderPadding, borderBottom: `1px solid ${fibeTokens.color.border}` }}>
                    <Box component="h2" sx={{ margin: 0, fontFamily: fibeTokens.font.family, ...fibeTokens.font.sectionHeading, color: fibeTokens.color.textPrimary }}>
                        Sub-Agents
                    </Box>
                </Box>

                {isLoading ? (
                    <AgentsTableSkeleton />
                ) : errorMessage ? (
                    <Box sx={{ padding: '16px' }}>
                        <ErrorBanner title="Could not load sub-agents" message={errorMessage} onRetry={loadAgents} />
                    </Box>
                ) : agents.length === 0 ? (
                    <SubAgentsEmptyState onRegister={() => setModal({ type: 'register' })} />
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table sx={{ width: '100%', minWidth: '1360px', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell size="fibe" variant="head" align="left" sx={{ width: 110 }}>Code</TableCell>
                                    <TableCell size="fibe" variant="head" align="left">Name</TableCell>
                                    <TableCell size="fibe" variant="head" align="left" sx={{ width: 110 }}>Ratio</TableCell>
                                    <TableCell size="fibe" variant="head" align="left" sx={{ width: 260 }}>Linked Institutes</TableCell>
                                    <TableCell size="fibe" variant="head" align="right" sx={{ width: 110 }}>Earned</TableCell>
                                    <TableCell size="fibe" variant="head" align="right" sx={{ width: 110 }}>Paid</TableCell>
                                    <TableCell size="fibe" variant="head" align="right" sx={{ width: 110 }}>Pending</TableCell>
                                    <TableCell size="fibe" variant="head" align="right" sx={{ width: 110 }}>Held</TableCell>
                                    <TableCell size="fibe" variant="head" align="left" sx={{ width: 320 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {agents.map((agent) => (
                                    <TableRow key={agent.id}>
                                        <TableCell size="fibe" variant="body" align="left" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 700, fontSize: 11.5, color: fibeTokens.color.textPrimary }}>
                                            {agent.agentCode}
                                        </TableCell>
                                        <TableCell size="fibe" variant="body" align="left">{agent.name}</TableCell>
                                        <TableCell size="fibe" variant="body" align="left">
                                            <Chip variant="fibePill" color="default" label={`${agent.ratio}:${100 - agent.ratio}`} />
                                        </TableCell>
                                        <TableCell size="fibe" variant="body" align="left">
                                            <Box sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: fibeTokens.color.textMuted }}>
                                                {linkedInstitutesLabel(agent.id)}
                                            </Box>
                                        </TableCell>
                                        <TableCell size="fibe" variant="body" align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                            {formatCurrency(computeEarned(agent))}
                                        </TableCell>
                                        <TableCell size="fibe" variant="body" align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: fibeTokens.color.success }}>
                                            {formatCurrency(agent.totalPaid)}
                                        </TableCell>
                                        <TableCell size="fibe" variant="body" align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                            {formatCurrency(agent.totalPayable)}
                                        </TableCell>
                                        <TableCell
                                            size="fibe"
                                            variant="body"
                                            align="right"
                                            sx={{
                                                fontVariantNumeric: 'tabular-nums',
                                                color: agent.totalHeld > 0 ? fibeTokens.color.warning : fibeTokens.color.textDisabled,
                                                fontWeight: agent.totalHeld > 0 ? 600 : 400,
                                            }}
                                        >
                                            {formatCurrency(agent.totalHeld)}
                                        </TableCell>
                                        <TableCell size="fibe" variant="body" align="left">
                                            <Box sx={{ display: 'flex', gap: '6px' }}>
                                                <Button variant="fibeSecondary" size="small" onClick={() => setLedgerAgentId(agent.id)}>Ledger</Button>
                                                <Button variant="fibeSuccess" size="small" onClick={() => setModal({ type: 'pay', agent })}>Pay</Button>
                                                <Button variant="fibeSecondary" size="small" onClick={() => setModal({ type: 'link', agent })}>Link</Button>
                                                <Button variant="fibeDangerOutline" size="small" onClick={() => setModal({ type: 'delink', agent })}>De-link</Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </Box>

            {ledgerAgent && <LedgerPanel agent={ledgerAgent} onClose={() => setLedgerAgentId(null)} />}

            {modal?.type === 'register' && <RegisterSubAgentModal onClose={closeModal} onRegistered={loadAgents} />}
            {modal?.type === 'link' && (
                <LinkSubAgentModal
                    agents={agents}
                    institutes={institutes}
                    initialAgent={modal.agent}
                    onClose={closeModal}
                    onLinked={loadAgents}
                />
            )}
            {modal?.type === 'pay' && (
                <PaySubAgentModal agent={modal.agent} onClose={closeModal} onPaid={loadAgents} />
            )}
            {modal?.type === 'delink' && (
                <DelinkSubAgentModal
                    agent={modal.agent}
                    instituteNameById={instituteNameById}
                    onClose={closeModal}
                    onDelinked={loadAgents}
                />
            )}
        </div>
    );
}

export default SubAgentsTab;
